import { ModelType } from '@/modules/core/app/constants/ModelType';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { Document } from '@/modules/document/models/Document';
import { ExtractionType } from '@/modules/ai/constants/ExtractionType';
import { DocumentExtractionStatus } from '@/modules/ai/constants/DocumentExtractionStatus';
import { ExtractedUserCollection } from '@/modules/ai/models/ExtractedUserCollection';
import { ExtractedTransaction } from '@/modules/ai/models/ExtractedTransaction';
import { ExtractedAssetCollection } from '@/modules/ai/models/ExtractedAssetCollection';
import { DocumentExtractionBankStatementMeta } from '@/modules/ai/models/meta/DocumentExtractionBankStatementMeta';
import { DocumentExtractionMetaFactory } from '@/modules/ai/models/meta/DocumentExtractionMetaFactory';
import { DocumentExtractionMetaType } from '@/modules/ai/constants/DocumentExtractionMetaType';
import { FinancialAccount } from '@/modules/financialaccount/models/FinancialAccount';
import { ExtractedOpportunity } from '@/modules/ai/models/ExtractedOpportunity';
import { hasOverlap } from '@/modules/core/app/helpers/DateHelper';
import { Opportunity } from '@/modules/opportunity/models/Opportunity';
import Lang from '@/modules/core/app/services/Lang';

export class DocumentExtraction extends BaseEntityModel {
  id?: string;
  documentId?: number;
  extractionType?: ExtractionType;
  metaType?: DocumentExtractionMetaType;
  isValidated?: boolean;
  status?: string;
  classifiedSubType?: string;
  classificationConfidence?: number;
  classificationReasoning?: string;
  failureReasonCode?: string;
  summaryEn?: string;
  summaryFr?: string;
  pageCount?: number;
  meta?: DocumentExtractionBankStatementMeta | undefined;
  document?: Relationship<Document | null>;
  extractedUsers!: Relationship<ExtractedUserCollection>;
  extractedTransactions: Relationship<ExtractedTransaction[]>;
  extractedAssets!: Relationship<ExtractedAssetCollection>;
  extractedOpportunities: Relationship<ExtractedOpportunity[]>;

  constructor(model: Partial<DocumentExtraction> = {}) {
    super(model);

    this.id = model.id;
    this.documentId = model.documentId;
    this.extractionType = model.extractionType;
    this.metaType = model.metaType;
    this.isValidated = model.isValidated;
    this.status = model.status;
    this.classifiedSubType = model.classifiedSubType;
    this.classificationConfidence = model.classificationConfidence;
    this.classificationReasoning = model.classificationReasoning;
    this.failureReasonCode = model.failureReasonCode;
    this.summaryEn = model.summaryEn;
    this.summaryFr = model.summaryFr;
    this.pageCount = model.pageCount;

    this.meta = model.meta
      ? DocumentExtractionMetaFactory.build(model.metaType, model.meta)
      : undefined;
    this.document = Document.mapModelOrNull(model.document);
    this.extractedUsers = ExtractedUserCollection.mapModel(model.extractedUsers);
    this.extractedTransactions = ExtractedTransaction.mapModels(model.extractedTransactions);
    this.extractedAssets = ExtractedAssetCollection.mapModel(model.extractedAssets);
    this.extractedOpportunities = ExtractedOpportunity.mapModels(model.extractedOpportunities);
  }

  getModelName(): ModelType {
    return ModelType.DOCUMENT_EXTRACTION;
  }

  get isStep895621WillExecutorsType(): boolean {
    return this.extractionType === ExtractionType.STEP_895621_WILL_EXECUTORS;
  }

  get isWillExecutorsType(): boolean {
    return this.extractionType === ExtractionType.WILL_EXECUTORS;
  }

  get isWillResidualBeneficiariesType(): boolean {
    return this.extractionType === ExtractionType.WILL_RESIDUAL_BENEFICIARIES;
  }

  get isWillSpecificBeneficiariesType(): boolean {
    return this.extractionType === ExtractionType.WILL_SPECIFIC_BENEFICIARIES;
  }

  get isSecureWillAndCodicilType(): boolean {
    return (
      this.isWillExecutorsType ||
      this.isWillResidualBeneficiariesType ||
      this.isWillSpecificBeneficiariesType
    );
  }

  get summary(): string | undefined {
    return (Lang.isFrench() ? this.summaryFr : this.summaryEn) ?? this.summaryEn;
  }

  get isStopped(): boolean {
    return this.status === DocumentExtractionStatus.STOPPED;
  }

  get isProcessing(): boolean {
    return this.status === DocumentExtractionStatus.PROCESSING;
  }

  get isCompleted(): boolean {
    return this.status === DocumentExtractionStatus.COMPLETED;
  }

  get isFailed(): boolean {
    return this.status === DocumentExtractionStatus.FAILED;
  }

  get pendingOpportunitiesCount(): number {
    const opportunities: ExtractedOpportunity[] = this.extractedOpportunities ?? [];
    return opportunities.filter(
      (extracted: ExtractedOpportunity): boolean => !extracted.opportunity?.isReviewed
    ).length;
  }

  get hasPendingOpportunities(): boolean {
    return this.pendingOpportunitiesCount > 0;
  }

  updateExtractedOpportunity(opportunity: Opportunity): void {
    if (!this.extractedOpportunities?.length || !opportunity) {
      return;
    }

    const extractedOpportunity: ExtractedOpportunity | undefined = this.extractedOpportunities.find(
      (extracted: ExtractedOpportunity): boolean => extracted.opportunityId === opportunity.id
    );

    if (extractedOpportunity) {
      extractedOpportunity.opportunity = opportunity;
    }
  }

  get pendingUsersCount(): number {
    return this.extractedUsers?.pendingUsers.length ?? 0;
  }

  get pendingAssetsCount(): number {
    return this.extractedAssets?.pendingAssets.length ?? 0;
  }

  hasOverlappingDates(account: FinancialAccount): boolean {
    if (!account.bankStatements?.length) {
      return false;
    }

    return account.bankStatements.some((statement: Document): boolean =>
      statement
        .documentExtractions!.get()!
        .some(
          (otherExtraction: DocumentExtraction): boolean =>
            otherExtraction.meta!.startDate &&
            otherExtraction.meta!.endDate &&
            hasOverlap(
              this.meta?.startDate,
              this.meta?.endDate,
              otherExtraction.meta!.startDate,
              otherExtraction.meta!.endDate
            )
        )
    );
  }
}
